import $console from '@zuto/utilities/src/$console'
import { ApplicationFormAnswers, PersonalDetailsType } from '../components/ApplicationForm/ApplicationFormContext'
import { AttributionDetails } from '../components/ApplicationForm/types'
import PersonalDetailsValidator from './PersonalDetailsValidator'
import EmailAddressValidator from './EmailAddressValidator'
import TitleValidator from './TitleValidator'
import VehicleTypeValidator from './VehicleTypeValidator'
import DrivingLicenceValidator from './DrivingLicenceValidator'
import LoanAmountValidator from './LoanAmountValidator'
import { sourcesMissingDrivingLicence, sourcesMissingVehicleType } from '../utils/utils-sources'
import { phoneNumberRegex } from '../constants'
import AddressHistoryValidator from './AddressHistoryValidator'

const NO_LOAN_AMOUNT_SPECIFIED = 'Not sure yet'

export const isCustomerDetailsValid = (
  prefillData: ApplicationFormAnswers,
  source: AttributionDetails['source'],
  lenderName?: string
): boolean => {  
  // Prefill maps dateOfBirth onto the root of ApplicationFormAnswers, not onto personalDetails,
  // but PersonalDetailsValidator expects it nested under personalDetails.
  const personalDetails: PersonalDetailsType = {
    ...prefillData.personalDetails,
    dateOfBirth: prefillData.personalDetails?.dateOfBirth ?? prefillData.dateOfBirth
  }
  const answers: ApplicationFormAnswers = { ...prefillData, personalDetails }

  // Sources we know don't send a usable driving licence (it gets defaulted in the mappings) always pass,
  // whatever the lender or value. For everyone else a missing licence only matters when the lender is zuto.
  const isDrivingLicenceValid = () => {
    const isDrivingLicenceExemptSource = Boolean(sourcesMissingDrivingLicence.includes(source.toLowerCase()))
    if (isDrivingLicenceExemptSource) return true

    const isMissingDrivingLicenceAcceptable = !answers.drivingLicence && lenderName !== 'zuto'
    return isMissingDrivingLicenceAcceptable || DrivingLicenceValidator().validateState(answers)
  }

  // Prefill defaults loanAmount to 'Not sure yet' when the customer never specified one - that's
  // a valid answer in its own right, not something that should fail validation.
  const isLoanAmountValid = () =>
    !answers.loanAmount ||
    answers.loanAmount === NO_LOAN_AMOUNT_SPECIFIED ||
    LoanAmountValidator({ source }).validateState(answers)

  // VehicleType
  const isVehicleTypeValid = () => {
    const isVehicleTypeExemptSource = Boolean(sourcesMissingVehicleType.includes(source.toLowerCase()))
    const isMissingVehicleTypeAcceptable = !answers.vehicleType && isVehicleTypeExemptSource
    return isMissingVehicleTypeAcceptable || VehicleTypeValidator().validateState(answers)
  }

  const isAddressHistoryValid = () => 
    Boolean(answers.addressHistory?.entries?.['1']) &&
    AddressHistoryValidator.LengthOfTimeValidator({ source }).validateState(answers)

  const checks: Record<string, () => boolean> = {
    vehicleType: isVehicleTypeValid,
    drivingLicence: isDrivingLicenceValid,
    loanAmount: isLoanAmountValid,
    emailAddress: () => EmailAddressValidator().validateState(personalDetails),
    personalDetails: () => PersonalDetailsValidator().validateState(answers),
    title: () => TitleValidator().validateState(answers),
    addressHistory: isAddressHistoryValid
  }

  // Run every check (no short-circuit) so the log names all failing fields. Field names only - never values (PII).
  // customerId is our opaque prefill reference (already logged in getPrefillDetails), so it's safe to log.
  const failedFields = Object.keys(checks).filter(field => !checks[field]())

  if (failedFields.length > 0) {
    $console.info(JSON.stringify({
      eventType: 'CustomerDetailsValidationFailed',
      source,
      lender: lenderName ?? 'none',
      customerId: prefillData.customerId ?? 'none',
      failedFields
    }))
  }

  return failedFields.length === 0
}

export const isContactNumberValid = (
  contactNumber: string | undefined | null
): boolean => {
  return Boolean(
    contactNumber
    && (contactNumber as string).match(phoneNumberRegex)
  )
}